import { useStrings } from "@1js/localization-components";
import type { ButtonProps } from "@fluentui/react-components";
import {
  MenuItem,
  Spinner,
  ToggleButton,
  Tooltip,
} from "@fluentui/react-components";
import {
  BookmarkFilled,
  BookmarkOffFilled,
  BookmarkOffRegular,
  bundleIcon,
  DeleteFilled,
  DeleteRegular,
} from "@fluentui/react-icons";
import * as React from "react";
import type { Appearance } from "../../data";
import {
  DeleteButtonTooltip,
  DeletingLabel,
  RemoveTooltip,
} from "./PromptDelete.strings";
import { useStyles } from "./PromptDelete.styles";

export type PromptDeleteProps = {
  appearance?: Appearance;
  isDeleting?: boolean;
  onDelete?: () => void;
  tabIndex?: number;
  isPromptLikingEnabled?: boolean;
  renderAsMenuItem?: boolean;
};

const DeleteIcon = bundleIcon(DeleteFilled, DeleteRegular);
const BookmarkOffIcon = bundleIcon(BookmarkOffFilled, BookmarkOffRegular);

export const PromptDelete: React.FC<PromptDeleteProps> = ({
  appearance,
  isDeleting,
  onDelete,
  tabIndex,
  isPromptLikingEnabled,
  renderAsMenuItem,
}) => {
  const styles = useStyles(appearance)();
  const localizations = useStrings({
    RemoveTooltip,
    DeletingLabel,
    DeleteButtonTooltip,
  });

  const buttonStyleProps: ButtonProps = {
    appearance: "transparent",
  };

  const handleOnClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    onDelete && onDelete();
  };

  return renderAsMenuItem ? (
    <MenuItem
      icon={<BookmarkOffIcon />}
      data-testid="delete-button"
      onClick={handleOnClick}
      aria-label={localizations.DeleteButtonTooltip()}
    >
      {localizations.DeleteButtonTooltip()}
    </MenuItem>
  ) : (
    <div className={styles.deletes}>
      <Tooltip content={localizations.RemoveTooltip()} relationship="label">
        <ToggleButton
          data-testid="delete-button"
          className={isDeleting ? styles.spinner : styles.delete}
          {...buttonStyleProps}
          size="small"
          onClick={handleOnClick}
          aria-busy={isDeleting}
          checked={!isPromptLikingEnabled}
          tabIndex={tabIndex}
        >
          {isDeleting ? (
            <Spinner
              size="tiny"
              labelPosition="after"
              label={localizations.DeletingLabel()}
              data-testid="delete-spinner"
            />
          ) : (
            <span className={styles.span}>
              {isPromptLikingEnabled ? (
                <DeleteIcon className={styles.icon} />
              ) : (
                <BookmarkFilled className={styles.icon} />
              )}
            </span>
          )}
        </ToggleButton>
      </Tooltip>
    </div>
  );
};
